"use client"
import {UiErrorText} from "@/components/i18n/ui-error-text"
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"

import { useQualitySections } from "@/hooks/use-quality-presentations"


import { useEffect, useRef, useState } from "react"
import { ArrowRight, Hourglass } from "lucide-react"

import { KpiCard, KpiCardSkeleton } from "@/components/ui/analytics/kpi-card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible"
import { Skeleton } from "@/components/ui/skeleton"
import {
  useMissedReplyConversationsQuery,
  useMissedReplyWordingsQuery,
} from "@/hooks/queries/conversation-quality"
import { usePermissions } from "@/hooks/use-permissions"
import { capitalize, cn } from "@/lib/utils"
import { SKIP_REASONS, skipReasonDisplay } from "@/lib/agents/messaging/skip-reasons"
import { MESSAGING_AGENTS_PERMISSIONS } from "@/lib/auth/permissions.constants"
import {
  MISSED_REPLY_PAGE_SIZE,
  missedReplyDetail,
  PAUSED_SPLITS,
  type MissedReplyDetail,
  type PausedSplit,
} from "@/lib/conversation-quality/missed-reply-detail"
import type {
  ConversationVolumeResult,
  QualityPeriodInput,
} from "@/server-actions/conversation-quality"

import { ConversationNameLink, CountedApart, EmptyNote, LANDING_SCROLL_MARGIN, RowTrigger, SectionCard, SessionLinks } from "./section-parts"
import { useTranslations } from "next-intl"



type MissedReply = ConversationVolumeResult["missedReplies"][number]
type ConversationsQuery = ReturnType<typeof useMissedReplyConversationsQuery>
type ConversationsPage = NonNullable<ConversationsQuery["data"]>["pages"][number]
type Conversation = NonNullable<ConversationsPage["conversations"]>[number]
type SplitCount = NonNullable<ConversationsPage["splits"]>[PausedSplit]

interface OwedCauses {
  rows: MissedReply[];
  total: number;
  nothingToAnswer: number;
}

function getWAITING(tUi: ReturnType<typeof useTranslations<"ConversationQuality.ConversationQualitySectionsMissedReplySections">>) {
  return {
  label: tUi("customersWaiting"),
  definition:
    "Customers who wrote while the agent was paused or off, and got no reply since — in the conversation or by email.",
}
}

const SPLIT_LABELS: Record<PausedSplit, string> = {
  nobody_answered: "Nobody answered",
  answered_later: "Answered later",
}

/**
 * Why the agent stayed silent, by cause. A cause opens into the conversations behind it — or, where the
 * agent decided so itself, into its own wording — for whoever may read them.
 */
export function MissedReplies({
  data,
  input,
  openCause,
  onOpened,
}: {
  data: ConversationVolumeResult | undefined;
  input: QualityPeriodInput;
  /** Opened on arrival, from the page address. */
  openCause: string | null;
  /** Called once that row is open, so a later reload of the rows does not open and scroll to it again. */
  onOpened: () => void;
}) {
  const tUi = useTranslations("ConversationQuality.ConversationQualitySectionsMissedReplySections")

  const { can } = usePermissions()
  const canRead = can(MESSAGING_AGENTS_PERMISSIONS.VIEW)
  const causes = data && owedCauses(data.missedReplies)

  return (
    <SectionCard title={tUi("missedReplies")} subtitle={causes && owedSubtitle(tUi, causes.total)}>
      {causes ? (
        <CauseList causes={causes} input={input} canRead={canRead} openCause={openCause} onOpened={onOpened} />
      ) : (
        <Skeleton className="h-24 w-full" />
      )}
    </SectionCard>
  )
}

/**
 * Overview's call to act on Missed replies: customers who wrote while the agent was paused and nobody has answered.
 * It reads the Volume row's own query, so opening that row reuses it.
 */
export function CustomersWaitingTile({ input, onOpenAll }: { input: QualityPeriodInput; onOpenAll: () => void }) {
  const {locale: uiFormattingLocale} = useUiControlLabels()

  const tUi = useTranslations("ConversationQuality.ConversationQualitySectionsMissedReplySections")
  const WAITING = getWAITING(tUi)


  const query = useMissedReplyConversationsQuery({
    ...input,
    cause: SKIP_REASONS.AI_PAUSED_OR_DISABLED,
    split: "nobody_answered",
  })

  if (query.error) return <DetailError error={query.error} />
  const waiting = query.data?.pages[0]?.splits?.nobody_answered
  if (!waiting) return <KpiCardSkeleton />

  return (
    <KpiCard
      label={WAITING.label}
      tooltip={WAITING.definition}
      icon={Hourglass}
      formattedValue={waiting.conversations.toLocaleString(uiFormattingLocale)}
      subtitle={waiting.conversations === 0 ? "nobody is waiting" : `no reply yet · ${questionShare(tUi, waiting)}`}
      footer={
        waiting.conversations > 0 ? (
          <Button variant="link" size="sm" className="h-auto px-0" onClick={onOpenAll}>
            <span><span key={tUi("seeWho")}>{tUi("seeWho")}</span></span>
            <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        ) : undefined
      }
    />
  )
}

/** A non-actionable message was owed no answer: counted apart, as noise is under «How sessions ended». */
function owedCauses(rows: MissedReply[]): OwedCauses {
  const owed = rows.filter((row) => row.cause !== SKIP_REASONS.NON_ACTIONABLE)
  return {
    rows: owed,
    total: owed.reduce((sum, row) => sum + row.count, 0),
    nothingToAnswer: rows.find((row) => row.cause === SKIP_REASONS.NON_ACTIONABLE)?.count ?? 0,
  }
}

function owedSubtitle(tUi: ReturnType<typeof useTranslations<"ConversationQuality.ConversationQualitySectionsMissedReplySections">>, total: number): string {
  return tUi("value0InboundMessagesThatWereOwedAnAnswerPlural", {count: total})
}

function CauseList({
  causes: { rows, total, nothingToAnswer },
  input,
  canRead,
  openCause,
  onOpened,
}: {
  causes: OwedCauses;
  input: QualityPeriodInput;
  canRead: boolean;
  openCause: string | null;
  onOpened: () => void;
}) {
  const allAnswered = nothingToAnswer > 0 ? "Every message that was owed an answer got one." : "Every message got a reply."

  return (
    <div className="space-y-6">
      {total > 0 ? (
        <ul className="-my-3 divide-y">
          {rows.map((row) => (
            <MissedReplyRow
              key={row.cause}
              row={row}
              total={total}
              input={input}
              canRead={canRead}
              openOnArrival={row.cause === openCause}
              onOpened={onOpened}
            />
          ))}
        </ul>
      ) : (
        <EmptyNote text={allAnswered} />
      )}
      <CountedApart
        count={nothingToAnswer}
        one="1 more needed no answer — a story mention, a sticker or an empty tap."
        many="more needed no answer — story mentions, stickers, empty taps."
      />
    </div>
  )
}

function MissedReplyRow({
  row,
  total,
  input,
  canRead,
  openOnArrival,
  onOpened,
}: {
  row: MissedReply;
  total: number;
  input: QualityPeriodInput;
  canRead: boolean;
  openOnArrival: boolean;
  onOpened: () => void;
}) {
  const ref = useRef<HTMLLIElement>(null)
  const detail = missedReplyDetail(row.cause)
  const opens = rowOpens(detail, canRead)

  useEffect(() => {
    if (!openOnArrival || !opens) return
    ref.current?.scrollIntoView({ block: "start" })
    onOpened()
  }, [openOnArrival, opens, onOpened])

  const heading = <RowHeading row={row} total={total} />
  if (!opens) return <li className="py-3 pl-6">{heading}</li>

  return (
    <li ref={ref} className={cn("py-3", LANDING_SCROLL_MARGIN)}>
      <Collapsible defaultOpen={openOnArrival}>
        <RowTrigger>{heading}</RowTrigger>
        {/* Mounted only while open, so its queries run only then. */}
        <CollapsibleContent className="ml-6 mt-3">
          <RowDetail detail={detail} cause={row.cause} input={input} />
        </CollapsibleContent>
      </Collapsible>
    </li>
  )
}

/** The wording and the split counts name no customer, so analytics alone opens them; a list of conversations needs `view`. */
function rowOpens(detail: MissedReplyDetail, canRead: boolean): boolean {
  switch (detail) {
    case "none":
      return false
    case "wordings":
    case "split_conversations":
      return true
    case "conversations":
      return canRead
    default: {
      const exhaustive: never = detail
      return exhaustive
    }
  }
}

function RowHeading({ row, total }: { row: MissedReply; total: number }) {
  const {locale: uiFormattingLocale} = useUiControlLabels()

  const { label, hint } = skipReasonDisplay(row.cause)

  return (
    <div className="min-w-0 flex-1 space-y-1">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span>{label}</span>
        <span className="font-medium tabular-nums">{row.count.toLocaleString(uiFormattingLocale)}</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-primary" style={{ width: `${(row.count / total) * 100}%` }} />
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

function RowDetail({
  detail,
  cause,
  input,
}: {
  detail: MissedReplyDetail;
  cause: string;
  input: QualityPeriodInput;
}) {
  switch (detail) {
    case "none":
      return null
    case "wordings":
      return <Wordings input={input} />
    case "conversations":
      return <Conversations input={input} cause={cause} />
    case "split_conversations":
      return <SplitConversations input={input} cause={cause} />
    default: {
      const exhaustive: never = detail
      return exhaustive
    }
  }
}

/** Whether anyone answered after the agent stepped back: the conversations still waiting come first. */
function SplitConversations({ input, cause }: { input: QualityPeriodInput; cause: string }) {
  const {locale: uiFormattingLocale} = useUiControlLabels()


  const tUi = useTranslations("ConversationQuality.ConversationQualitySectionsMissedReplySections")

  const [split, setSplit] = useState<PausedSplit>("nobody_answered")
  const query = useMissedReplyConversationsQuery({ ...input, cause, split })
  const splits = query.data?.pages[0]?.splits
  const current = splits?.[split]

  return (
    <div className="space-y-3">
      <div role="group" aria-label={tUi("whetherAnyoneAnswered")} className="flex flex-wrap gap-1">
        {PAUSED_SPLITS.map((value) => (
          <Button
            key={value}
            size="sm"
            variant={value === split ? "secondary" : "ghost"}
            aria-pressed={value === split}
            onClick={() => setSplit(value)}
          >
            <span key={splits ? tUi("value0Value1", { value0: String(SPLIT_LABELS[value]), value1: String(splits[value].conversations.toLocaleString(uiFormattingLocale)) }) : SPLIT_LABELS[value]}>{splits ? tUi("value0Value1", { value0: String(SPLIT_LABELS[value]), value1: String(splits[value].conversations.toLocaleString(uiFormattingLocale)) }) : SPLIT_LABELS[value]}</span>
          </Button>
        ))}
      </div>
      {current && current.conversations > 0 && (
        <p className="text-xs text-muted-foreground">{capitalize(questionShare(tUi, current))}</p>
      )}
      <ConversationPages query={query} timezone={input.timezone} />
    </div>
  )
}

function Conversations({ input, cause }: { input: QualityPeriodInput; cause: string }) {
  const query = useMissedReplyConversationsQuery({ ...input, cause, split: null })
  return <ConversationPages query={query} timezone={input.timezone} />
}

function ConversationPages({ query, timezone }: { query: ConversationsQuery; timezone: string }) {

  const tUi = useTranslations("ConversationQuality.ConversationQualitySectionsMissedReplySections")

  if (query.error) return <DetailError error={query.error} />
  if (!query.data) return <Skeleton className="h-24 w-full" />
  // Withheld from someone who may not read conversations; the split's counts above still show.
  if (query.data.pages[0]?.conversations === null) return null

  const conversations = query.data.pages.flatMap((page) => page.conversations ?? [])
  if (conversations.length === 0) return <EmptyNote text={tUi("noConversationHere")} />

  return (
    <div className="space-y-2">
      <ul aria-label={tUi("conversationsBehindThisCause")} className="space-y-2">
        {conversations.map((conversation) => (
          <ConversationRow key={conversation.conversationId} conversation={conversation} timezone={timezone} />
        ))}
      </ul>
      {query.hasNextPage && (
        <Button
          variant="link"
          size="sm"
          className="h-auto px-0"
          disabled={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        >
          <span key={query.isFetchingNextPage ? tUi("loading") : tUi("showValue0More", { value0: String(MISSED_REPLY_PAGE_SIZE) })}>{query.isFetchingNextPage ? tUi("loading") : tUi("showValue0More", { value0: String(MISSED_REPLY_PAGE_SIZE) })}</span>
        </Button>
      )}
    </div>
  )
}

/** The contact's name opens the conversation; a silent handover says why above the customer's message. */
function ConversationRow({ conversation, timezone }: { conversation: Conversation; timezone: string }) {
  const { referenceLabel, channelLabel } = useQualitySections()


  const tUi = useTranslations("ConversationQuality.ConversationQualitySectionsMissedReplySections")

  return (
    <li className="space-y-0.5 rounded-md border px-3 py-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <ConversationNameLink href={conversation.reference.href} name={referenceLabel(conversation.reference)} />
        <span className="text-xs tabular-nums text-muted-foreground">
          {<span key={tUi("value0Value1", { value0: String(channelLabel(conversation.channel)), value1: String(formatMarkerTime(conversation.markerAt, timezone)) })}>{tUi("value0Value1", { value0: String(channelLabel(conversation.channel)), value1: String(formatMarkerTime(conversation.markerAt, timezone)) })}</span>}
        </span>
      </div>
      {conversation.handoverReason && (
        <p className="line-clamp-2 break-words text-sm">{<span key={tUi("agentSReasonValue0", { value0: String(conversation.handoverReason) })}>{tUi("agentSReasonValue0", { value0: String(conversation.handoverReason) })}</span>}</p>
      )}
      <p key={conversation.lastMessage ? tUi("value0", { value0: String(conversation.lastMessage) }) : tUi("noTextInTheCustomerSLastMessage")} className="line-clamp-2 break-words text-sm text-muted-foreground">
        {conversation.lastMessage ? tUi("value0", { value0: String(conversation.lastMessage) }) : tUi("noTextInTheCustomerSLastMessage")}
      </p>
    </li>
  )
}

/** One wording for «how many of these end on a question», wherever the number is shown. */
function questionShare(tUi: ReturnType<typeof useTranslations<"ConversationQuality.ConversationQualitySectionsMissedReplySections">>, { conversations, endingOnQuestion }: SplitCount): string {
  return tUi("value0OfValue1EndOnAQuestion", { value0: String(endingOnQuestion.toLocaleString()), value1: String(conversations.toLocaleString()) })
}

function Wordings({ input }: { input: QualityPeriodInput }) {
  const {locale: uiFormattingLocale} = useUiControlLabels()

  const tUi = useTranslations("ConversationQuality.ConversationQualitySectionsMissedReplySections")

  const { data, error } = useMissedReplyWordingsQuery(input)

  if (error) return <DetailError error={error} />
  if (!data) return <Skeleton className="h-24 w-full" />
  if (data.length === 0) return <EmptyNote text={tUi("noReasonWasRecorded")} />

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground"><span key={tUi("inTheAgentSOwnWordsTheMost")}>{tUi("inTheAgentSOwnWordsTheMost")}</span></p>
      <ul className="space-y-3">
        {data.map((wording) => (
          <li key={wording.wording} className="space-y-1.5">
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="min-w-0 break-words">“{wording.wording}”</span>
              <span className="shrink-0 text-muted-foreground tabular-nums">{wording.count.toLocaleString(uiFormattingLocale)}</span>
            </div>
            {wording.references && <SessionLinks label={tUi("in")} sessions={wording.references} />}
          </li>
        ))}
      </ul>
    </div>
  )
}

function DetailError({ error }: { error: Error }) {
  return (
    <Alert variant="destructive">
      <AlertDescription><UiErrorText message={error.message} /></AlertDescription>
    </Alert>
  )
}

function formatMarkerTime(at: string, timezone: string): string {
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: timezone,
  }).format(new Date(at))
}
